import React, { useState } from 'react';
import { Sofa, Tag, Coins, ShoppingCart, RefreshCw, ArrowUpRight } from 'lucide-react';
import { CREDITS_PER_IMAGE, STAGING_FEATURES, STAGING_FULL_RENOVATION_CREDITS, STAGING_PREMIUM_CREDITS, STAGING_TAGLINE, STAGING_VIDEO } from '../constants';
import { CREDIT_EXPIRY_COPY, formatV3UnitRate, minorToMajor, REHUB_CREDIT_MONTHLY_PLANS_V3, REHUB_CREDIT_TOPUPS_V3 } from '@rehub/pricing';
import { AskAiSection, ECOSYSTEM_LOGO_SLOT_CLASS, buildAskAiPrompt, buildAskAiProviders, ecosystemLogoClass } from '@rehub/ask-ai';

type RehubApp = { name: string; tagline: string; description: string; url: string; logo: string; current?: boolean };

const REHUB_APPS: RehubApp[] = [
  { name: 'PhotoEdit', tagline: 'AI Photo Editing', description: 'Professional AI photo editing — tone, colour, detail and crop for every listing image.', url: 'https://photoedit.rehub.studio', logo: 'https://photoedit.rehub.studio/photoedit-logo.png' },
  { name: 'HDR Photo', tagline: 'AI HDR Merging', description: 'Merge bracketed exposures into studio-quality HDR, then enhance automatically.', url: 'https://hdrphoto.rehub.studio', logo: 'https://hdrphoto.rehub.studio/hdr-photo-logo.png' },
  { name: 'Photoclear.ai', tagline: 'AI Decluttering', description: 'Remove clutter and distractions instantly with AI to reveal clean, buyer-ready rooms.', url: 'https://photoclear.rehub.studio', logo: 'https://photoclear.rehub.studio/Photoclear Logo HR Trans.png' },
  { name: 'Virtual Staging', tagline: 'AI Virtual Staging', description: 'Transform empty rooms into beautifully furnished interiors in minutes.', url: 'https://staging.rehub.studio', logo: 'https://staging.rehub.studio/virtual-staging-logo-transparent.png', current: true },
  { name: 'ImageMotion', tagline: 'AI Motion', description: 'Turn static listing photos into cinematic property videos automatically.', url: 'https://imagemotion.rehub.studio', logo: 'https://imagemotion.rehub.studio/Logo.webp' },
  { name: 'VideoMotion', tagline: 'AI Video Editing', description: 'Turn raw walkthrough footage into polished, social-ready video edits.', url: 'https://videomotion.rehub.studio', logo: 'https://videomotion.rehub.studio/logo.png' },
  { name: 'PropertyCuts', tagline: 'Video Assembler', description: 'Stitch photos, clips and floor plans into a finished property video.', url: 'https://propertycuts.rehub.studio', logo: 'https://propertycuts.rehub.studio/logo.png' },
  { name: 'Hyper Dusk', tagline: 'AI Day-to-Dusk', description: 'Transform daytime exteriors into stunning dusk shots with AI-powered lighting.', url: 'https://hyperdusk.rehub.studio', logo: 'https://hyperdusk.rehub.studio/hyperdusk-logo-wordmark-alpha.png' },
  { name: 'Screen Record', tagline: 'Guided Recordings', description: 'Record guided 360° tours and screen walkthroughs with voice, camera and a shareable link.', url: 'https://record.rehub.studio', logo: '/screenrecord-logo.png' },
  { name: '360 Tours', tagline: 'Virtual Tours', description: 'Build immersive virtual tours for property listings and developments.', url: 'https://360tours.rehub.studio', logo: 'https://360tours.rehub.studio/360tours-studio-logo-transparent.png' },
  { name: 'Planos', tagline: 'Interactive Floor Plans', description: 'Create interactive floor plans that let buyers explore properties visually.', url: 'https://planos.rehub.studio', logo: 'https://planos.rehub.studio/Planos Logo Red.png' },
  { name: 'Sketchplan', tagline: 'AI Floor Plans', description: 'Turn rough sketches into accurate, professional floor plans.', url: 'https://sketchplan.rehub.studio', logo: 'https://sketchplan.rehub.studio/cropped-Sketchplan-Logo1-removebg-preview.webp' },
];

const ASK_AI_PRODUCT = 'Virtual Staging Studio';
const ASK_AI_PROVIDERS = buildAskAiProviders(ASK_AI_PRODUCT);
const ASK_AI_PROMPT = buildAskAiPrompt({
  productName: ASK_AI_PRODUCT,
  siteUrl: 'https://staging.rehub.studio',
  askAiUrl: 'https://virtualstaging.studio/ask-ai',
  extraFocus:
    'Focus on AI virtual staging for empty property rooms, furniture styles, 360 panorama staging where published, and the 5-credit staged-image pricing.',
});

const EcosystemGrid: React.FC = () => (
  <div className="max-w-6xl mx-auto mt-28">
    <div className="text-center mb-12">
      <p className="text-sm font-semibold uppercase tracking-widest text-teal-400 mb-4">The ReHub Ecosystem</p>
      <h2 className="text-3xl md:text-4xl font-bold text-slate-100 mb-3">One platform. Every property marketing app.</h2>
      <p className="text-slate-400 max-w-2xl mx-auto">Each app is purpose-built for property and works beautifully on its own — or together as one seamless workflow.</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {REHUB_APPS.map((app) => (
        <a
          key={app.name}
          href={app.url}
          target={app.current ? undefined : '_blank'}
          rel={app.current ? undefined : 'noopener noreferrer'}
          className="group flex flex-col rounded-2xl border border-slate-700/70 bg-slate-900/50 backdrop-blur-sm p-6 text-left transition-all hover:border-slate-500 hover:bg-slate-900/80"
        >
          <div className={`mb-5 ${ECOSYSTEM_LOGO_SLOT_CLASS}`}>
            <img src={app.logo} alt={app.name} className={ecosystemLogoClass(app.name)} />
          </div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{app.tagline}</p>
          <p className="text-sm text-slate-400 leading-relaxed mb-5 grow">{app.description}</p>
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-200 group-hover:text-white transition-colors">
            {app.current ? "You're here" : 'Launch app'}
            {!app.current && <ArrowUpRight size={16} className="text-slate-500 group-hover:text-teal-300 transition-colors" />}
          </span>
        </a>
      ))}
    </div>
  </div>
);

type Currency = 'GBP' | 'USD';

function defaultCurrency(): Currency {
  if (typeof window === 'undefined') return 'USD';
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz === 'Europe/London') return 'GBP';
    if ((navigator.language || '').startsWith('en-GB')) return 'GBP';
  } catch (_) {}
  return 'USD';
}

// Derived from the live @rehub/pricing catalogue — do not hardcode amounts here.
const TOPUP_PACKS = REHUB_CREDIT_TOPUPS_V3.map((p) => ({
  credits: p.credits,
  GBP: minorToMajor(p.amountMinor.GBP),
  USD: minorToMajor(p.amountMinor.USD),
  perCreditGBP: formatV3UnitRate(p.amountMinor.GBP, p.credits, 'GBP'),
  perCreditUSD: formatV3UnitRate(p.amountMinor.USD, p.credits, 'USD'),
  popular: p.badge === 'popular',
}));

const SUBSCRIPTION_PLANS = REHUB_CREDIT_MONTHLY_PLANS_V3.map((p) => ({
  name: p.label,
  credits: p.credits,
  GBP: minorToMajor(p.amountMinor.GBP),
  USD: minorToMajor(p.amountMinor.USD),
  perCreditGBP: formatV3UnitRate(p.amountMinor.GBP, p.credits, 'GBP'),
  perCreditUSD: formatV3UnitRate(p.amountMinor.USD, p.credits, 'USD'),
  popular: p.badge === 'popular',
}));

interface LandingPageProps {
  onSignUp: () => void;
  onLogin: () => void;
  onOpenPricing?: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onSignUp, onLogin, onOpenPricing }) => {
  const [currency, setCurrency] = useState<Currency>(defaultCurrency);
  const sym = currency === 'GBP' ? '£' : '$';

  return (
    <main className="relative z-10">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#8A2BE2]/20 text-[#A855F7] rounded-full text-sm font-medium mb-8">
            <Sofa size={14} className="shrink-0" />
            Virtual Staging Studio
          </div>
          {/* Logo - hero, prominent size */}
          <div className="flex justify-center mb-6">
            <img
              src="/virtual-staging-logo-transparent.png"
              alt="Virtual Staging Studio"
              className="h-20 md:h-28 w-auto object-contain drop-shadow-lg"
            />
          </div>
          <h1 className="text-5xl font-bold mb-6 leading-tight text-white">
            Stage Empty Rooms
            <br />
            <span className="bg-gradient-to-r from-[#8A2BE2] to-[#A855F7] bg-clip-text text-transparent">
              in Minutes
            </span>
          </h1>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            {STAGING_TAGLINE}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={onSignUp}
              className="group flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-[#8A2BE2] to-[#9B3CF3] hover:from-[#9B3CF3] hover:to-[#A855F7] text-white rounded-2xl font-semibold text-lg transition-all shadow-xl shadow-[#8A2BE2]/30 hover:shadow-[#8A2BE2]/40 hover:scale-105 border border-[#8A2BE2]/30"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 group-hover:rotate-[360deg] transition-transform duration-700">
                <Sofa size={20} strokeWidth={2} />
              </span>
              Get Started Free
            </button>
            <button
              onClick={onLogin}
              className="px-8 py-5 bg-slate-900/80 backdrop-blur-sm border-2 border-slate-500 hover:border-slate-400 text-slate-200 hover:text-white rounded-2xl font-semibold text-lg transition-all shadow-lg"
            >
              Sign In
            </button>
            {onOpenPricing && (
              <button
                onClick={onOpenPricing}
                className="inline-flex items-center gap-2 px-6 py-5 bg-teal-600 hover:bg-teal-500 text-white rounded-2xl font-medium text-base transition-all shadow-lg"
              >
                <Tag size={18} />
                Pricing & benefits
              </button>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-4">
            10 free credits on signup. No credit card required.
          </p>

          <div className="mt-12 w-full max-w-3xl mx-auto rounded-2xl overflow-hidden border border-slate-700/80 shadow-2xl shadow-[#8A2BE2]/20">
            <div className="relative w-full pt-[56.25%] bg-black">
              <iframe
                src={STAGING_VIDEO.embedUrl}
                title={STAGING_VIDEO.title}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 text-left">
            {STAGING_FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="p-6 bg-slate-900/70 backdrop-blur-md border border-slate-700/80 rounded-2xl hover:border-[#8A2BE2]/30 transition-colors"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Sofa size={18} className="text-[#8A2BE2] shrink-0" />
                  <h3 className="text-lg font-semibold text-slate-100">{feature.title}</h3>
                </div>
                <p className="text-sm text-slate-400">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Pricing */}
          <section id="pricing" className="mt-24 text-left scroll-mt-24">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#8A2BE2]/20 text-[#A855F7] rounded-full text-sm font-medium mb-4">
                <Tag size={14} className="shrink-0" />
                Simple, credit-based pricing
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Pay only for what you stage
              </h2>
              <p className="text-slate-400 mt-3 max-w-2xl mx-auto">
                Buy credits once and use them across every ReHub app. No subscriptions required.
              </p>
              <div className="flex items-center justify-center gap-2 mt-6">
                {(['GBP', 'USD'] as Currency[]).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCurrency(c)}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${currency === c ? 'bg-[#8A2BE2] text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'}`}
                  >
                    {c === 'GBP' ? '£ UK' : '$ US'}
                  </button>
                ))}
              </div>
            </div>

            {/* Credits intro */}
            <div className="flex items-start gap-3 p-4 rounded-xl bg-[#8A2BE2]/10 border border-[#8A2BE2]/30 mb-8 max-w-2xl mx-auto">
              <Coins size={20} className="text-[#A855F7] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-[#C084FC]">{CREDITS_PER_IMAGE} credits standard · {STAGING_PREMIUM_CREDITS} Ultra Luxury · {STAGING_FULL_RENOVATION_CREDITS} full renovation</p>
                <p className="text-sm text-slate-400 mt-1">
                  10 free credits on sign-up. Same credits work across all ReHub apps — buy once, use anywhere.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {/* One-time top-ups */}
              <div>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">
                  <ShoppingCart size={16} className="text-[#A855F7]" />
                  One-time credit packs
                </h3>
                <div className="rounded-xl border border-slate-700 overflow-hidden bg-slate-900/70">
                  <div className="grid grid-cols-3 gap-3 px-4 py-2.5 bg-slate-800/60 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    <span>Credits</span>
                    <span className="text-right">Price</span>
                    <span className="text-right">Per credit</span>
                  </div>
                  {TOPUP_PACKS.map((pack, i) => (
                    <div
                      key={pack.credits}
                      className={`grid grid-cols-3 gap-3 px-4 py-2.5 items-center ${i > 0 ? 'border-t border-slate-800' : ''} ${pack.popular ? 'bg-[#8A2BE2]/10' : ''}`}
                    >
                      <span className="text-slate-200 font-medium">
                        {pack.credits}
                        {pack.popular && <span className="ml-2 text-[10px] font-bold text-[#A855F7] uppercase">Popular</span>}
                      </span>
                      <span className="text-right text-slate-300 tabular-nums">
                        {sym}{currency === 'GBP' ? pack.GBP.toFixed(2) : pack.USD.toFixed(2)}
                      </span>
                      <span className="text-right text-[#A855F7] font-semibold tabular-nums text-sm">
                        {currency === 'GBP' ? pack.perCreditGBP : pack.perCreditUSD}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Monthly subscriptions */}
              <div>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">
                  <RefreshCw size={16} className="text-[#A855F7]" />
                  Monthly credit plans
                </h3>
                <div className="rounded-xl border border-slate-700 overflow-hidden bg-slate-900/70">
                  <div className="grid grid-cols-4 gap-2 px-4 py-2.5 bg-slate-800/60 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    <span>Plan</span>
                    <span className="text-right">Cr/mo</span>
                    <span className="text-right">Price</span>
                    <span className="text-right">/credit</span>
                  </div>
                  {SUBSCRIPTION_PLANS.map((plan, i) => (
                    <div
                      key={plan.name}
                      className={`grid grid-cols-4 gap-2 px-4 py-2.5 items-center ${i > 0 ? 'border-t border-slate-800' : ''} ${plan.popular ? 'bg-[#8A2BE2]/10' : ''}`}
                    >
                      <span className="text-slate-200 font-medium">
                        {plan.name}
                        {plan.popular && <span className="ml-1.5 text-[10px] font-bold text-[#A855F7] uppercase">Popular</span>}
                      </span>
                      <span className="text-right text-slate-300 tabular-nums">{plan.credits.toLocaleString()}</span>
                      <span className="text-right text-slate-300 tabular-nums">
                        {sym}{currency === 'GBP' ? plan.GBP.toFixed(2) : plan.USD.toFixed(2)}
                      </span>
                      <span className="text-right text-[#A855F7] font-semibold tabular-nums text-sm">
                        {currency === 'GBP' ? plan.perCreditGBP : plan.perCreditUSD}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <p className="text-xs text-slate-500 mt-4 text-center">{CREDIT_EXPIRY_COPY.benefitsModal}</p>

            <div className="mt-8 max-w-md mx-auto">
              <button
                type="button"
                onClick={onSignUp}
                className="block w-full py-4 px-6 bg-gradient-to-r from-[#8A2BE2] to-[#9B3CF3] hover:from-[#9B3CF3] hover:to-[#A855F7] text-white font-semibold rounded-xl transition-all shadow-lg shadow-[#8A2BE2]/30 text-center"
              >
                Sign up — 10 free credits, no card required
              </button>
              {onOpenPricing && (
                <button
                  type="button"
                  onClick={onOpenPricing}
                  className="block w-full mt-3 text-center text-sm text-slate-400 hover:text-slate-200 transition-colors"
                >
                  See full pricing & benefits
                </button>
              )}
            </div>
          </section>
        </div>

        <EcosystemGrid />
        <AskAiSection
          productName={ASK_AI_PRODUCT}
          prompt={ASK_AI_PROMPT}
          providers={ASK_AI_PROVIDERS}
          overviewHref="https://virtualstaging.studio/ask-ai"
          description="Ask ChatGPT, Perplexity, Grok, Claude or Gemini about Virtual Staging Studio’s furniture styles, pricing and whether it fits your listing workflow."
        />
      </div>

    </main>
  );
};

export default LandingPage;
